import React from 'react';
import { Body5, Body7, Container } from '@ui';
import { isSetupActive } from '@services/fmdc';
import { DefList } from './DefList';
import { StatusPill } from './StatusPill';
import { PassiveCard, SectionHead, ErrorTone } from './styles';
import { formatCount, formatDateTime, formatSetupStatus, EM_DASH } from './format';
import { SETUP_STATUS_TOKENS, tokenFor } from './statusTokens';

// The client as GET /tenant describes it: template, catalog counts, and the last setup. Shown on
// Settings for everyone (members read it, admins act beside it) and as the body of the
// setting-up gate while a first setup runs.

const catalogRows = (catalog) => {
  if (!catalog) return [];
  const byStatus = catalog.by_status || {};
  const statusLine = Object.keys(byStatus).length
    ? Object.entries(byStatus)
        .map(([status, n]) => `${formatCount(n)} ${status}`)
        .join(', ')
    : null;
  return [
    { label: 'Catalog items', value: `${formatCount(catalog.items)}${statusLine ? ` (${statusLine})` : ''}` },
    { label: 'Aliases', value: formatCount(catalog.aliases) },
    { label: 'Pending proposals', value: formatCount(catalog.proposals_pending) },
    {
      label: 'Embeddings',
      value: `${formatCount(catalog.embeddings)}${
        catalog.embedded_at ? `, embedded ${formatDateTime(catalog.embedded_at)}` : ''
      }`,
    },
  ];
};

export const SetupStatusPill = ({ setup }) => {
  if (!setup?.status) return null;
  const label = setup.stale ? `${formatSetupStatus(setup.status)} (stuck)` : formatSetupStatus(setup.status);
  return (
    <StatusPill
      token={setup.stale ? 'negative' : tokenFor(SETUP_STATUS_TOKENS, setup.status)}
      label={label}
      pulse={!setup.stale && isSetupActive(setup)}
    />
  );
};

export const TenantCard = ({ tenant, canAdmin = false, pendingSetup = null }) => {
  const setup = tenant?.setup || pendingSetup;

  const aboutRows = tenant
    ? [
        { label: 'Client', value: tenant.name || EM_DASH },
        { label: 'Data capture', value: tenant.enabled === false ? 'Switched off' : 'Enabled' },
        { label: 'Template', value: tenant.template_version || EM_DASH },
        { label: 'Template file', value: tenant.template_filename },
        { label: 'Static_Info spec', value: tenant.static_info_template_spec_id },
        ...catalogRows(tenant.catalog),
      ]
    : [];

  const setupRows = setup
    ? [
        { label: 'Template', value: setup.template_version },
        { label: 'Queued', value: setup.queued_at ? formatDateTime(setup.queued_at) : null },
        { label: 'Started', value: setup.started_at ? formatDateTime(setup.started_at) : null },
        { label: 'Finished', value: setup.finished_at ? formatDateTime(setup.finished_at) : null },
        ...(canAdmin
          ? [
              { label: 'Requested by', value: setup.requested_by },
              {
                label: 'Result',
                value:
                  setup.result && typeof setup.result === 'object'
                    ? Object.entries(setup.result)
                        .map(([k, v]) => `${k}: ${formatCount(v)}`)
                        .join(', ')
                    : setup.result,
              },
            ]
          : []),
      ]
    : [];

  return (
    <PassiveCard>
      {tenant && (
        <>
          <SectionHead heading="Client" />
          <Container top={12}>
            <DefList rows={aboutRows} />
          </Container>
          {tenant.catalog?.embeddings_stale && (
            <Container top={12}>
              <Body7 isLowContrast role="status">
                Catalog refreshing… aliases and embeddings are being rebuilt after an edit.
              </Body7>
            </Container>
          )}
        </>
      )}
      {setup && (
        <Container top={tenant ? 24 : 0}>
          <Container row verticallyCenter gap={12}>
            <SectionHead heading="Last setup" />
            <SetupStatusPill setup={setup} />
          </Container>
          <Container top={12}>
            <DefList rows={setupRows} />
          </Container>
          {setup.error && canAdmin && (
            <Container top={12}>
              <ErrorTone>
                <Body5>{setup.error}</Body5>
              </ErrorTone>
            </Container>
          )}
        </Container>
      )}
    </PassiveCard>
  );
};
